import React, { useMemo, useState } from 'react';
import {
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Box,
  TextField,
  Paper,
  IconButton,
  CircularProgress,
  Divider,
  InputBase,
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import useAppStore from 'store/useAppStore';
import api from 'services/api';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import HistoryArea from './HistoryArea';
import getAllSegScriptForApi from 'utils/getAllSegScriptForApi';
import { getScripts } from 'utils/getScript';
function FullScriptEditorDialogChild({ fullScriptId, onClose }) {
  const showNotification = useAppStore((state) => state.showNotification);
  const editedFullScript = useAppStore((state) => state.editedFullScript);
  const setEditedFullScript = useAppStore((state) => state.setEditedFullScript);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const chatMessagesRef = React.useRef(null);
  const [save2DBFullScript, { isLoading: save2DBFullScriptLoading }] =
    api.endpoints.save2DBFullScript.useMutation();
  const [fullScriptChat, { isLoading: isChatLoading }] =
    api.endpoints.fullScriptChat.useMutation();

  const handleSave = async () => {
    if (!editedFullScript.trim()) {
      showNotification('請輸入全局腳本內容', 'error');
      return;
    }
    save2DBFullScript({
      hand_transp: useAppStore.getState().materialDevices,
      station: useAppStore.getState().stations,
      all_seg_script: getAllSegScriptForApi(),
      full_script_id: fullScriptId,
      project_id: useAppStore.getState().selectedProject.project_id,
      user_id: useAppStore.getState().user.id,
      comp_name: useAppStore.getState().user.company,
      edit_time: DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss'),
      full_script: editedFullScript,
    })
      .unwrap()
      .then(() => {
        showNotification('全局腳本儲存成功！', 'success');
      })
      .catch((error) => {
        console.error('儲存全局腳本錯誤:', error);
        showNotification('儲存全局腳本發生錯誤', 'error');
      });
  };

  const handleCancel = () => {
    onClose();
  };

  // 自動捲動到聊天區域底部
  const scrollToBottom = () => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) {
      return;
    }

    // 添加用戶訊息到聊天記錄
    const userMessage = {
      id: Date.now(),
      text: newMessage,
      sender: 'user',
      timestamp: new Date().toLocaleString(),
    };
    const updatedChatMessages = [...chatMessages, userMessage];
    setChatMessages(updatedChatMessages);

    // // 將聊天內容存入 store 中的全局腳本物件
    // if (fullScript) {
    //   updateFullScript('chat', updatedChatMessages);
    // }

    // 自動捲動到聊天區域底部
    setTimeout(scrollToBottom, 100);

    const currentMessage = newMessage;
    setNewMessage('');

    // 調用聊天 API
    fullScriptChat({
      session_id: sessionId || uuidv4(),
      project_id: useAppStore.getState().selectedProject.project_id,
      full_script_id: fullScriptId,
      full_script: editedFullScript,
      question: currentMessage,
    })
      .unwrap()
      .then((result) => {
        setSessionId(result.session_id);
        // 添加 AI 回應到聊天記錄
        const aiMessage = {
          id: Date.now() + 1,
          text: result.reply,
          sender: 'ai',
          timestamp: new Date().toLocaleString(),
        };
        const finalChatMessages = [...updatedChatMessages, aiMessage];
        setChatMessages(finalChatMessages);

        // 將包含 AI 回應的聊天內容存入 store 中的全局腳本物件
        // if (fullScript) {
        //   updateFullScript('chat', finalChatMessages);
        // }

        // 自動捲動到聊天區域底部
        setTimeout(scrollToBottom, 100);

        // 如果有優化後的全局腳本，直接覆蓋腳本內容
        if (result.reply_full_script) {
          setEditedFullScript(result.reply_full_script);
          // 同時更新 store 中的全局腳本內容
          // if (fullScript) {
          //   updateFullScript('full_script', result.replyFullScript);
          // }
          showNotification('全局腳本內容已自動更新！', 'success');
        }
      })
      .catch(() => {
        showNotification('發生錯誤，請稍後再試', 'error');
      });
  };

  return (
    <>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6" component="div">
            編輯全局腳本
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box className="flex min-h-0 flex-1 pb-2">
        {/* 左側區域 - 腳本內容和歷史紀錄 */}
        <Box className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex min-h-0 flex-7 flex-col px-2">
            <div className="flex items-center justify-between py-2">
              <span className="font-bold">全局腳本內容(可編輯)</span>
              <Button
                size="small"
                loading={save2DBFullScriptLoading}
                onClick={handleSave}
                variant="contained"
              >
                儲存
              </Button>
            </div>
            <Paper variant="outlined" className="flex flex-1 overflow-y-auto">
              <InputBase
                className="p-2"
                sx={{
                  alignItems: 'flex-start', // 讓 flex 容器內部元素靠上
                  '& .MuiInputBase-input': {
                    height: '100%', // 填滿容器高度
                    padding: '8px', // 控制內距
                    boxSizing: 'border-box',
                    alignSelf: 'flex-start', // 文字靠上
                  },
                }}
                fullWidth
                multiline
                value={editedFullScript}
                onChange={(e) => setEditedFullScript(e.target.value)}
              />
            </Paper>
          </div>
          {/* 歷史紀錄區塊 */}
          <div className="flex min-h-0 flex-3 px-2">
            <HistoryArea
              fullScriptId={fullScriptId}
              setEditedFullScript={setEditedFullScript}
            />
          </div>
        </Box>

        {/* 右側聊天室區域 */}
        <Box className="flex min-w-0 flex-1 flex-col pr-2">
          <div className="p-3 font-bold">與大語言模型對話</div>
          <Paper
            className="flex min-h-0 flex-1 flex-col p-2"
            variant="outlined"
          >
            <Box
              ref={chatMessagesRef}
              className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2"
            >
              {chatMessages.map((message) => (
                <Box key={message.id} sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent:
                        message.sender === 'user' ? 'flex-end' : 'flex-start',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {message.timestamp}
                    </Typography>
                  </Box>
                  <Paper
                    sx={{
                      p: 1,
                      backgroundColor:
                        message.sender === 'user'
                          ? (theme) =>
                              theme.palette.mode === 'dark'
                                ? '#1976d2'
                                : '#e3f2fd'
                          : (theme) =>
                              theme.palette.mode === 'dark'
                                ? '#9c27b0'
                                : '#f3e5f5',
                      maxWidth: '80%',
                      ml: message.sender === 'user' ? 'auto' : 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color:
                          message.sender === 'user'
                            ? (theme) =>
                                theme.palette.mode === 'dark'
                                  ? '#ffffff'
                                  : 'text.primary'
                            : (theme) =>
                                theme.palette.mode === 'dark'
                                  ? '#ffffff'
                                  : 'text.primary',
                      }}
                    >
                      {message.text}
                    </Typography>
                  </Paper>
                </Box>
              ))}
            </Box>
            <Box
              sx={{ display: 'flex', gap: 1 }}
              component="form"
              onSubmit={handleSendMessage}
            >
              <TextField
                fullWidth
                size="small"
                placeholder="輸入訊息..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                disabled={isChatLoading}
              />
              <Button
                variant="contained"
                size="small"
                type="submit"
                loading={isChatLoading}
                disabled={!newMessage.trim() || isChatLoading}
              >
                發送
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </>
  );
}
const FullScriptEditorDialog = ({ onClose }) => {
  const full_script_id = useAppStore(
    (state) => state.selectedProject.full_script_id,
  );
  const readOnly = useAppStore((state) => state.selectedProject.readOnly);
  const hand_transp = useAppStore((state) => state.materialDevices);
  const stations = useAppStore((state) => state.stations);
  const scripts = useAppStore((state) => state.scripts);
  const related_project_id = useAppStore(
    (state) => state.selectedRelatedProject,
  );
  const ori_project_id = useAppStore(
    (state) => state.selectedProject.ori_project_id,
  );
  const related_project_id_keys = useMemo(() => {
    const keys = Object.keys(related_project_id);
    return keys.length > 0 ? JSON.stringify(keys) : JSON.stringify([]);
  }, [related_project_id]);
  const project_id = useAppStore((state) => state.selectedProject.project_id);
  const user_id = useAppStore((state) => state.user.id);
  const comp_name = useAppStore((state) => state.user.company);
  // const { currentData, isFetching, isError, refetch } =
  //   api.endpoints.genFullScript.useQuery({
  const {
  currentData,
  isFetching,
  isError,
  error,
  refetch,} = api.endpoints.genFullScript.useQuery({
      hand_transp,
      station: stations,
      all_seg_script: getScripts(scripts),
      full_script_id: full_script_id,
      related_project_id: related_project_id_keys,
      ori_project_id,
      project_id,
      user_id,
      comp_name,
    });
  if (!currentData && isFetching) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <CircularProgress />
      </div>
    );
  }
  // if (isError) {
  //   return (
  //     <div className="flex flex-col items-center p-2">
  //       生成全局腳本發生錯誤
  //       <Button component="span" onClick={refetch}>
  //         重試
  //       </Button>
  //     </div>
  //   );
  // }
  if (isError) {
  console.error('genFullScript API錯誤：', error);

  return (
    <div className="flex flex-col items-center gap-2 p-2">
      <div>生成全局腳本發生錯誤</div>

      <pre className="max-w-full whitespace-pre-wrap text-red-600">
        {JSON.stringify(error, null, 2)}
      </pre>

      <Button component="span" onClick={refetch}>
        重試
      </Button>
    </div>
  );
}
  if (readOnly) {
    return (
      <>
        <DialogTitle className="flex items-center justify-between">
          <div>全局腳本內容</div>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography className="whitespace-pre-wrap">
            {currentData.full_script}
          </Typography>
        </DialogContent>
      </>
    );
  }
  return (
    <FullScriptEditorDialogChild
      fullScriptId={full_script_id}
      onClose={onClose}
    />
  );
};
export default FullScriptEditorDialog;
